import { z } from 'zod';

/**
 * Referral source options for "How did you find modcar?" dropdown
 * TODO: Add i18n keys for labels
 */
export const REFERRAL_SOURCE_OPTIONS = [
  { value: 'google_search', label: 'Google search' },
  { value: 'recommendation', label: 'Recommendation from another dealer' },
  { value: 'social_media', label: 'Social media' },
  { value: 'article_blog', label: 'Online article / blog' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'event_fair', label: 'Event or trade fair' },
  { value: 'added_to_team', label: 'Added to an existing modcar team' },
  { value: 'other', label: 'Other' },
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCE_OPTIONS)[number]['value'];

/**
 * Password signup schema - account creation only
 * CONTRACT v2.0.0 §1.1: Company/phone collected AFTER verification at CreateTeam page
 */
export const combinedSignupSchema = z
  .object({
    // Account fields
    email: z.string().min(1, 'E-mail is verplicht').email('Voer een geldig e-mailadres in'),
    password: z
      .string()
      .min(1, 'Wachtwoord is verplicht')
      .min(8, 'Wachtwoord moet minimaal 8 karakters zijn'),
    confirmPassword: z.string().min(1, 'Bevestig je wachtwoord'),

    // Name fields
    firstName: z
      .string()
      .min(1, 'Voornaam is verplicht')
      .min(2, 'Voornaam moet minimaal 2 karakters zijn')
      .max(50, 'Voornaam mag maximaal 50 karakters zijn'),
    lastName: z
      .string()
      .min(1, 'Achternaam is verplicht')
      .min(2, 'Achternaam moet minimaal 2 karakters zijn')
      .max(50, 'Achternaam mag maximaal 50 karakters zijn'),

    // Referral source (required dropdown)
    referralSource: z.enum(
      [
        'google_search',
        'recommendation',
        'social_media',
        'article_blog',
        'advertisement',
        'event_fair',
        'added_to_team',
        'other',
      ],
      { required_error: 'Selecteer hoe je modcar hebt gevonden' }
    ),
    // Conditional: required only when referralSource === 'other'
    referralOther: z.string().max(200, 'Maximaal 200 karakters').optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Wachtwoorden komen niet overeen',
    path: ['confirmPassword'],
  })
  .refine(
    (data) => {
      // If referralSource is 'other', referralOther must be filled
      if (data.referralSource === 'other') {
        return data.referralOther && data.referralOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Vul in hoe je modcar hebt gevonden',
      path: ['referralOther'],
    }
  );

export type CombinedSignupFormValues = z.infer<typeof combinedSignupSchema>;

/**
 * Invite signup schema - no company fields required
 * Used when user is signing up via an invite link
 */
export const inviteSignupSchema = z
  .object({
    // Account fields
    email: z.string().min(1, 'E-mail is verplicht').email('Voer een geldig e-mailadres in'),
    password: z
      .string()
      .min(1, 'Wachtwoord is verplicht')
      .min(8, 'Wachtwoord moet minimaal 8 karakters zijn'),
    confirmPassword: z.string().min(1, 'Bevestig je wachtwoord'),

    // Name fields
    firstName: z
      .string()
      .min(1, 'Voornaam is verplicht')
      .min(2, 'Voornaam moet minimaal 2 karakters zijn')
      .max(50, 'Voornaam mag maximaal 50 karakters zijn'),
    lastName: z
      .string()
      .min(1, 'Achternaam is verplicht')
      .min(2, 'Achternaam moet minimaal 2 karakters zijn')
      .max(50, 'Achternaam mag maximaal 50 karakters zijn'),

    // Optional company fields (not required for invite flow)
    companyName: z.string().optional(),
    phone: z.string().optional(),

    // Referral source (required dropdown)
    referralSource: z.enum(
      [
        'google_search',
        'recommendation',
        'social_media',
        'article_blog',
        'advertisement',
        'event_fair',
        'added_to_team',
        'other',
      ],
      { required_error: 'Selecteer hoe je modcar hebt gevonden' }
    ),
    // Conditional: required only when referralSource === 'other'
    referralOther: z.string().max(200, 'Maximaal 200 karakters').optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Wachtwoorden komen niet overeen',
    path: ['confirmPassword'],
  })
  .refine(
    (data) => {
      // If referralSource is 'other', referralOther must be filled
      if (data.referralSource === 'other') {
        return data.referralOther && data.referralOther.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Vul in hoe je modcar hebt gevonden',
      path: ['referralOther'],
    }
  );

export type InviteSignupFormValues = z.infer<typeof inviteSignupSchema>;
